import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import renderPrgImage from '../../../../../../source/programImg'
import ToggleSwitch from '../../../../../ReusableComponents/ToggleSwitch/ToggleSwitch'
import { loadPaths } from '../../../../../WebAi/vision/pathUtils'
import CameraColorPicker from '../../../../../WebAi/vision/CameraColorPicker'
import { COLOUR_DEFAULTS } from '../../../../../WebAi/vision/colourDetector'
import { loadOutputs } from '../../../../../WebAi/outputInstances'
import { loadEnabledVars, varById } from '../../../../../WebAi/aiVariables'
import {
    MARKER_DICTS,
    loadMarkerDict,
    saveMarkerDict,
} from '../../../../../WebAi/vision/arucoRuntime'
import './condition.css'
import './aiConditionPanel.css'
import closeImg from '../../../../../../Assets/img/close.png'
// AI-code condition panel. Same "If the value of ___ is [operators] [value]"
// layout as the original flowchart decision panel, but the sources are the AI
// detections chosen on the Select Feature screen (e.g. Face -> Face Recognition
// / Face Tracking). Value is compared with >, <, between, =, != (1 = detected).
const SOURCE_MAP = {
    // Position/tracking is handled by ROI regions + the Tracking (path) source,
    // so Face/Object only expose recognition here.
    'ai/face': [{ value: 'RECOGNIZE_FACE', label: 'Face Recognition' }],
    'ai/motion': [{ value: 'DETECT_MOTION', label: 'Motion' }],
    'ai/color': [{ value: 'DETECT_COLOR', label: 'Color' }],
    'ai/object': [
        { value: 'RECOGNIZE_OBJECT', label: 'Object Recognition' },
        { value: 'PART_COUNT', label: 'Object Count' },
    ],
    'ai/pose': [{ value: 'RECOGNIZE_POSE', label: 'Pose' }],
    // Gesture offers built-in MediaPipe gestures AND your own trained hand
    // gestures (hand-landmark classifier).
    'ai/gesture': [
        { value: 'RECOGNIZE_GESTURE', label: 'Gesture (built-in)' },
        { value: 'RECOGNIZE_HAND', label: 'Gesture (trained)' },
    ],
    'ai/voice': [{ value: 'SPEECH', label: 'Speech' }],
    // Microphone input = spoken-keyword detection (browser speech-to-text),
    // separate from the trained audio classifier used by the Voice A.I. feature.
    'inputs/microphone': [{ value: 'SPEECH_WORD', label: 'Microphone' }],
}

// Which way the object must travel the whole path (start -> end = Forward).
const PATH_DIR_OPTIONS = [
    { value: 'any', label: 'Any Direction' },
    { value: 'forward', label: 'Start → End' },
    { value: 'backward', label: 'End → Start' },
]

// MediaPipe GestureRecognizer's seven built-in gestures. Values match the
// runtime category names.
const GESTURE_OPTIONS = [
    { value: 'Thumb_Up', label: 'Thumbs Up' },
    { value: 'Thumb_Down', label: 'Thumbs Down' },
    { value: 'Open_Palm', label: 'Open Palm' },
    { value: 'Closed_Fist', label: 'Closed Fist' },
    { value: 'Victory', label: 'Victory' },
    { value: 'Pointing_Up', label: 'Pointing Up' },
    { value: 'ILoveYou', label: 'I Love You' },
]

const OPERATORS = [
    { key: 'greaterThan', img: 'greaterthan', label: 'Greater than' },
    { key: 'lessThan', img: 'lessthan', label: 'Less Than' },
    { key: 'inBetween', img: 'inbetween', label: 'In Between' },
    { key: 'equalTo', img: 'equalto', label: 'Equals to' },
    { key: 'notEqualTo', img: 'notequalto', label: 'Not Equals to' },
]

// Colour targets a rule can watch for. rgb is the reference colour; the runtime
// measures it as a direct RGB distance (contract 18.2) — tolerance is the max
// distance still counted as a match, coverage is the share of pixels that must
// be within tolerance, and consecutive is how many frames in a row must match
// before the rule fires. Presets are just quick starting colours; the picker
// lets the user pick any colour under their own lighting.
const COLOR_PRESETS = [
    { name: 'Red', rgb: [220, 40, 40] },
    { name: 'Orange', rgb: [240, 140, 30] },
    { name: 'Yellow', rgb: [240, 220, 40] },
    { name: 'Green', rgb: [40, 180, 70] },
    { name: 'Cyan', rgb: [40, 200, 210] },
    { name: 'Blue', rgb: [40, 90, 220] },
    { name: 'Purple', rgb: [150, 60, 210] },
    { name: 'Magenta', rgb: [220, 50, 180] },
]

const clamp255 = (v) => Math.max(0, Math.min(255, Math.round(v)))

// The target colour is chosen with one slider per channel.
const RGB_CHANNELS = [
    {
        key: 'R',
        color: '#d64541',
        gradient: 'linear-gradient(90deg, #FFE9C2 -8.7%, #d64541 100%)',
    },
    {
        key: 'G',
        color: '#27ae60',
        gradient: 'linear-gradient(90deg, #C8E6C9 -8.7%, #4CAF50 100%)',
    },
    {
        key: 'B',
        color: '#2f6fd6',
        gradient: 'linear-gradient(90deg, #C8E6C9 -8.7%, #2196F3 100%)',
    },
]

const rgbToHex = (rgb) =>
    '#' + rgb.map((v) => clamp255(v).toString(16).padStart(2, '0')).join('')

// Parse a hex string (with or without #, 3 or 6 digits) to [r,g,b], or null if
// it isn't a valid hex colour — so a pasted value only applies when complete.
const hexToRgb = (hex) => {
    let h = String(hex).trim().replace(/^#/, '')
    if (/^[0-9a-f]{3}$/i.test(h))
        h = h
            .split('')
            .map((c) => c + c)
            .join('')
    if (!/^[0-9a-f]{6}$/i.test(h)) return null
    const n = parseInt(h, 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Nearest preset name, or a hex label for a custom colour, so the rule reads
// naturally ("appears Red") even when the colour was hand-picked.
const nameForRgb = (rgb) => {
    let best = null
    let bestD = Infinity
    for (const p of COLOR_PRESETS) {
        const dr = rgb[0] - p.rgb[0]
        const dg = rgb[1] - p.rgb[1]
        const db = rgb[2] - p.rgb[2]
        const d = dr * dr + dg * dg + db * db
        if (d < bestD) {
            bestD = d
            best = p
        }
    }
    return bestD <= 40 * 40 ? best.name : rgbToHex(rgb).toUpperCase()
}

const makeColorTarget = (rgb, name) => ({
    rgb,
    name: name || nameForRgb(rgb),
    tolerance: COLOUR_DEFAULTS.tolerance,
    coverage: COLOUR_DEFAULTS.coverage,
    consecutive: COLOUR_DEFAULTS.consecutive,
    step: COLOUR_DEFAULTS.step,
})

// Keyboard keys a rule can watch for. Values match the runtime's normalised
// key names (lowercase; space spelled out).
const KEY_OPTIONS = [
    { value: 'any', label: 'Any Key' },
    ...'abcdefghijklmnopqrstuvwxyz'
        .split('')
        .map((c) => ({ value: c, label: c.toUpperCase() })),
    ...'0123456789'.split('').map((c) => ({ value: c, label: c })),
    { value: 'space', label: 'Space' },
    { value: 'enter', label: 'Enter' },
    { value: 'arrowup', label: 'Arrow Up' },
    { value: 'arrowdown', label: 'Arrow Down' },
    { value: 'arrowleft', label: 'Arrow Left' },
    { value: 'arrowright', label: 'Arrow Right' },
]

const MOUSE_OPTIONS = [
    { value: 'left', label: 'Left Click' },
    { value: 'right', label: 'Right Click' },
    { value: 'any', label: 'Any Button' },
]

// Common COCO classes offered for object tracking (predefined detector).
const OBJECT_OPTIONS = [
    { value: 'any', label: 'Any Object' },
    { value: 'person', label: 'Person' },
    { value: 'cell phone', label: 'Cell Phone' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'cup', label: 'Cup' },
    { value: 'book', label: 'Book' },
    { value: 'laptop', label: 'Laptop' },
    { value: 'chair', label: 'Chair' },
    { value: 'sports ball', label: 'Sports Ball' },
    { value: 'remote', label: 'Remote' },
]

// A counter/flag decision source encodes its variable id in the value, e.g.
// "VAR:Bic1", so no extra field is needed to persist the target.
const VAR_PREFIX = 'VAR:'
const varIdOf = (source) =>
    typeof source === 'string' && source.startsWith(VAR_PREFIX)
        ? source.slice(VAR_PREFIX.length)
        : null

// Sensible operator/value defaults per source. Detection sources are yes/no
// matches (1 = detected); counters default to "greater than 0", flags to "= On".
const defaultsFor = (source) => {
    const vid = varIdOf(source)
    if (vid) {
        return varById(vid)?.kind === 'flag'
            ? { operator: 'equalTo', value: 1 }
            : { operator: 'greaterThan', value: 0 }
    }
    return { operator: 'equalTo', value: 1 }
}

const loadSelectedFeatures = () => {
    try {
        return (
            JSON.parse(sessionStorage.getItem('aiCodeSelectedFeatures')) || []
        )
    } catch {
        return []
    }
}

const AiConditionPanel = (props) => {
    const id = props.check
    const enable = JSON.parse(sessionStorage.getItem('enable') || '{}')
    const selectedFeatures = loadSelectedFeatures()

    const sources = []
    selectedFeatures.forEach((f) => {
        ;(SOURCE_MAP[f] || []).forEach((s) => {
            if (!sources.some((x) => x.value === s.value)) sources.push(s)
        })
    })
    // Tracking (path crossing) — DeepSORT tracking of people/objects across the
    // paths drawn in Configure > Lines. Offered only when the Tracking A.I.
    // feature is selected (and needs a camera).
    if (enable?.s1?.cam && selectedFeatures.includes('ai/tracking'))
        sources.push({ value: 'PATH_CROSS', label: 'Tracking' })
    // OCR — read text off the camera (Tesseract). Offered when the OCR feature
    // is selected.
    if (enable?.s1?.cam && selectedFeatures.includes('ai/ocr'))
        sources.push({ value: 'OCR_TEXT', label: 'Text (OCR)' })
    // ArUco marker id — numeric compare (=, >, <). Offered when the marker
    // feature is selected.
    if (enable?.s1?.cam && selectedFeatures.includes('ai/marker'))
        sources.push({ value: 'MARKER_ID', label: 'ArUco Markers' })
    // Laptop input triggers, offered when the matching input was selected.
    if (selectedFeatures.includes('inputs/keyboard'))
        sources.push({ value: 'KEY_PRESS', label: 'Keyboard' })
    if (selectedFeatures.includes('inputs/mouse'))
        sources.push({ value: 'MOUSE_CLICK', label: 'Mouse Click' })
    // On-screen buttons = Button output instances placed in Configure > Output.
    const screenButtons = loadOutputs()
        .filter((o) => o.type === 'button')
        .map((o) => ({ id: o.id, label: o.name }))
    if (screenButtons.length)
        sources.push({ value: 'SCREEN_BUTTON', label: 'On-Screen Button' })
    // On-screen toggles = Toggle output instances placed in Configure > Output.
    const screenToggles = loadOutputs()
        .filter((o) => o.type === 'toggle')
        .map((o) => ({ id: o.id, label: o.name }))
    if (screenToggles.length)
        sources.push({ value: 'SCREEN_TOGGLE', label: 'On-Screen Toggle' })
    // Counters/flags enabled in the Start block, comparable as a decision.
    const enabledVars = loadEnabledVars()
    enabledVars.forEach((v) =>
        sources.push({ value: `${VAR_PREFIX}${v.id}`, label: v.name })
    )

    const readDetail = (key, fallback) => {
        try {
            const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
            const val = all[id]?.[key]
            return val === undefined || val === null ? fallback : val
        } catch {
            return fallback
        }
    }

    // Use the saved source only if its feature is still selected; otherwise fall
    // back to a currently-available one. Prevents a stale source (e.g. Face after
    // Face was deselected) from lingering with the wrong class list (bug 27).
    const savedSource = readDetail('source', null)
    const availableSourceValues = sources.map((s) => s.value)
    const initialSource =
        savedSource && availableSourceValues.includes(savedSource)
            ? savedSource
            : sources[0]?.value || ''
    const initialDefaults = defaultsFor(initialSource)
    const [source, setSource] = useState(initialSource)
    const [operator, setOperator] = useState(() =>
        readDetail('operator', initialDefaults.operator)
    )
    const [value, setValue] = useState(() =>
        readDetail('value', initialDefaults.value)
    )
    // Backfill RGB params onto an older stored target (pre-18.2 targets only had
    // hue/sat/val), so an existing rule keeps working after the update.
    const normaliseColorTarget = (t) => {
        if (!t || !Array.isArray(t.rgb))
            return makeColorTarget(COLOR_PRESETS[0].rgb, COLOR_PRESETS[0].name)
        return {
            rgb: t.rgb,
            name: t.name || nameForRgb(t.rgb),
            tolerance: t.tolerance ?? COLOUR_DEFAULTS.tolerance,
            coverage: t.coverage ?? COLOUR_DEFAULTS.coverage,
            consecutive: t.consecutive ?? COLOUR_DEFAULTS.consecutive,
            step: t.step ?? COLOUR_DEFAULTS.step,
        }
    }
    const [colorTarget, setColorTarget] = useState(() =>
        normaliseColorTarget(readDetail('colorTarget', null))
    )
    // Editable hex text for the colour field, kept in sync with the sliders so a
    // hex sampled in play mode can be pasted straight in.
    const [hexText, setHexText] = useState(() =>
        rgbToHex(normaliseColorTarget(readDetail('colorTarget', null)).rgb)
    )
    // Live camera eyedropper for the colour rule (open/closed).
    const [showColorPicker, setShowColorPicker] = useState(false)
    // Data-variable comparison: against a typed value, or against another data
    // variable. compareVar is which other variable to compare with.
    const [compareMode, setCompareMode] = useState(() =>
        readDetail('compareMode', 'value')
    )
    const [compareVar, setCompareVar] = useState(() =>
        readDetail('compareVar', '')
    )
    // ArUco dictionary the printed markers use (global detection setting).
    const [markerDict, setMarkerDict] = useState(loadMarkerDict)
    const [keyName, setKeyName] = useState(() => readDetail('keyName', 'space'))
    const [mouseButton, setMouseButton] = useState(() =>
        readDetail('mouseButton', 'left')
    )
    const [buttonId, setButtonId] = useState(() =>
        readDetail('buttonId', screenButtons[0]?.id || 'button1')
    )
    const [toggleId, setToggleId] = useState(() =>
        readDetail('toggleId', screenToggles[0]?.id || '')
    )
    const [gestureName, setGestureName] = useState(() =>
        readDetail('gestureName', GESTURE_OPTIONS[0].value)
    )
    const [speechWord, setSpeechWord] = useState(() =>
        readDetail('speechWord', '')
    )
    const [ocrText, setOcrText] = useState(() => readDetail('ocrText', ''))
    // Tracking (path crossing) selections. Default to a real drawn path so the
    // dropdown's shown value and the stored value always agree (an empty/stale
    // pathId is why a rule could silently never fire).
    const [pathId, setPathId] = useState(() => {
        const saved = readDetail('pathId', '')
        const paths = loadPaths()
        const valid = paths.some((p) => String(p.id) === String(saved))
        return valid ? saved : paths[0]?.id || ''
    })
    const [pathDir, setPathDir] = useState(() => readDetail('pathDir', 'any'))
    const [pathTarget, setPathTarget] = useState(() =>
        readDetail('pathTarget', 'person')
    )
    // Trajectory overlay preference (shared with Execute via sessionStorage,
    // default off). Toggled here for tracking rules and in the Execute stats.
    const [showTrace, setShowTrace] = useState(
        () => sessionStorage.getItem('aiShowTrajectory') === 'true'
    )
    const toggleTrace = (n) => {
        sessionStorage.setItem('aiShowTrajectory', String(n))
        setShowTrace(n)
    }

    const isTrainedRecognition =
        source === 'RECOGNIZE_FACE' ||
        source === 'RECOGNIZE_OBJECT' ||
        source === 'RECOGNIZE_POSE' ||
        source === 'RECOGNIZE_HAND'
    const isSpeech = source === 'SPEECH'
    const isColor = source === 'DETECT_COLOR'
    const isMotion = source === 'DETECT_MOTION'
    const isPathCross = source === 'PATH_CROSS'
    const isKey = source === 'KEY_PRESS'
    const isMouse = source === 'MOUSE_CLICK'
    const isScreenButton = source === 'SCREEN_BUTTON'
    const isScreenToggle = source === 'SCREEN_TOGGLE'
    const isGesture = source === 'RECOGNIZE_GESTURE'
    const isSpeechWord = source === 'SPEECH_WORD'
    const isOcr = source === 'OCR_TEXT'
    // Marker id is a numeric value (compare with =, >, <), not a boolean match.
    const isMarker = source === 'MARKER_ID'
    // Object Count: pick a trained object model; the value is the live count
    // (numeric, not a boolean match), so it is NOT a class/boolean source.
    const isPartCount = source === 'PART_COUNT'
    // Counter/flag comparison. Counters are numeric (all operators); flags are
    // boolean On/Off (equals / not-equals only).
    const varKind = varById(varIdOf(source))?.kind || null
    const isVariable = !!varKind
    const isVarFlag = varKind === 'flag'
    const isVarCounter = varKind === 'counter'
    // Data variables are numeric like counters, but can also be compared against
    // another data variable (see the Value/Variable toggle below).
    const isVarData = varKind === 'data'
    // Enabled data variables — comparing one against another only makes sense
    // when there are at least two, so the toggle is gated on that.
    const dataVars = enabledVars.filter((v) => v.kind === 'data')
    const otherDataVars = dataVars.filter((v) => v.id !== varIdOf(source))
    const showCompareToggle = isVarData && dataVars.length >= 2
    // Recognition rules (image face/object or audio speech/sound) all pick a
    // trained model + class the same way.
    const isClassRecognition = isTrainedRecognition || isSpeech
    // Boolean sources (matched / not matched) only support equals / not-equals.
    const isBooleanSource =
        isClassRecognition ||
        isColor ||
        isPathCross ||
        isKey ||
        isMouse ||
        isScreenButton ||
        isScreenToggle ||
        isGesture ||
        isMotion ||
        isSpeechWord ||
        isOcr ||
        isVarFlag

    // Paths drawn in Configure > Lines, and the target options for tracking:
    // built-in COCO classes plus any trained face/object classes.
    const drawnPaths = loadPaths()

    // Trained model + class + region (like ProgrammingPage's Face Detected).
    const allCamModels = useSelector(
        (s) => s.appModeStorage?.trainMachine?.camModels || []
    )
    const currentModelId = useSelector(
        (s) => s.appModeStorage?.trainMachine?.currentModelId
    )
    // The model TYPE this source needs. A model can be trained for several
    // types, so we offer models that have a trained head for this type
    // (trainedTypes) — and, if none yet, all models so it's still selectable.
    const requiredModelType =
        source === 'RECOGNIZE_FACE'
            ? 'face'
            : source === 'RECOGNIZE_OBJECT' || source === 'PART_COUNT'
              ? 'object'
              : source === 'RECOGNIZE_POSE'
                ? 'pose'
                : source === 'RECOGNIZE_HAND'
                  ? 'gesture'
                  : null
    const modelHasType = (m, t) =>
        m?.trainedTypes?.[t] ||
        // Legacy models trained before per-type heads: infer from current type.
        (m?.modelType || (m?.detectionType === 'pose' ? 'pose' : 'face')) === t
    const camModels = (() => {
        if (!requiredModelType) return allCamModels
        const matching = allCamModels.filter((m) =>
            modelHasType(m, requiredModelType)
        )
        return matching.length ? matching : allCamModels
    })()
    const [selectedModelId, setSelectedModelId] = useState(() =>
        readDetail('modelId', currentModelId || allCamModels[0]?.modelId || '')
    )
    const [selectedClassId, setSelectedClassId] = useState(() =>
        readDetail('selectedClassId', 'none')
    )
    const [selectedRegionId, setSelectedRegionId] = useState(() =>
        readDetail('selectedRegionId', 'none')
    )
    // If the currently selected model isn't valid for this source's type, fall
    // back to the first matching model so the class list is never mismatched.
    const selectedModel =
        camModels.find((m) => String(m.modelId) === String(selectedModelId)) ||
        camModels[0]
    // A class is only usable in a decision once it has captured samples (an
    // untrained, just-created class would never match at Execute). We surface
    // that so the panel can mark/disable classes that aren't trained yet.
    const imageDetails = Object.values(selectedModel?.ImageClasses || {})
        .filter(
            (c) =>
                !requiredModelType ||
                (c.classType || 'face') === requiredModelType
        )
        .map(({ id, className, capturedImagesCount }) => ({
            id,
            className,
            trained: (capturedImagesCount || 0) > 0,
        }))
    const audioDetails = Object.values(selectedModel?.AudioClasses || {}).map(
        ({ id, className, capturedAudioCount }) => ({
            id,
            className,
            trained: (capturedAudioCount || 0) > 0,
        })
    )
    const classList = isSpeech ? audioDetails : imageDetails
    const roiRegions = selectedModel?.roiRegions || []

    // Targets for path-crossing: built-in COCO classes + any trained face/object
    // class. A trained target ("trained:<modelId>:<classId>") tracks that
    // identity while all others are still tracked/shown.
    const pathTargetOptions = [...OBJECT_OPTIONS]
    allCamModels.forEach((m) => {
        const t = m.modelType || (m.detectionType === 'pose' ? 'pose' : 'face')
        if (t !== 'face' && t !== 'object') return
        Object.values(m.ImageClasses || {})
            .filter(
                (c) =>
                    (c.classType || 'face') === t &&
                    (c.capturedImagesCount || 0) > 0
            )
            .forEach((c) =>
                pathTargetOptions.push({
                    value: `trained:${m.modelId}:${c.id}`,
                    label: `${m.modelName} / ${c.className}`,
                })
            )
    })

    const persist = (patch) => {
        const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
        all[id] = { ...(all[id] || { id }), ...patch }
        sessionStorage.setItem('allDetails', JSON.stringify(all))
    }

    // Persist the current (possibly default) selection on mount so the decision
    // is evaluated even when the user doesn't change anything.
    useEffect(() => {
        persist({
            source,
            operator,
            value,
            modelId: selectedModel?.modelId || selectedModelId,
            selectedClassId,
            selectedRegionId,
            colorTarget,
            keyName,
            mouseButton,
            buttonId,
            toggleId,
            gestureName,
            speechWord,
            ocrText,
            compareMode,
            compareVar,
            pathId: pathId || drawnPaths[0]?.id || '',
            pathDir,
            pathTarget,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onModel = (e) => {
        const v = e.target.value
        setSelectedModelId(v)
        setSelectedClassId('none')
        setSelectedRegionId('none')
        persist({
            modelId: v,
            selectedClassId: 'none',
            selectedRegionId: 'none',
        })
    }
    const onClass = (e) => {
        setSelectedClassId(e.target.value)
        persist({ selectedClassId: e.target.value })
    }
    const onRegion = (e) => {
        setSelectedRegionId(e.target.value)
        persist({ selectedRegionId: e.target.value })
    }

    const onSource = (e) => {
        const v = e.target.value
        setSource(v)
        const d = defaultsFor(v)
        setOperator(d.operator)
        setValue(d.value)
        const patch = { source: v, operator: d.operator, value: d.value }
        if (v === 'DETECT_COLOR') {
            const target = normaliseColorTarget(readDetail('colorTarget', null))
            setColorTarget(target)
            setHexText(rgbToHex(target.rgb))
            patch.colorTarget = target
        }
        // Snap the pinned model to one that matches the new source's type, so
        // the model/class dropdowns aren't showing a mismatched model.
        const needType =
            v === 'RECOGNIZE_FACE'
                ? 'face'
                : v === 'RECOGNIZE_OBJECT'
                  ? 'object'
                  : v === 'RECOGNIZE_POSE'
                    ? 'pose'
                    : v === 'RECOGNIZE_HAND'
                      ? 'gesture'
                      : null
        if (needType) {
            const match =
                allCamModels.find((m) => modelHasType(m, needType)) ||
                allCamModels[0]
            if (match) {
                setSelectedModelId(match.modelId)
                setSelectedClassId('none')
                patch.modelId = match.modelId
                patch.selectedClassId = 'none'
            }
        }
        persist(patch)
    }
    // Apply a new RGB target from any source (sliders or hex), keeping the hex
    // text field, name and stored detail in sync. tolerance/coverage/frames are
    // kept at their (default) values — they drive matching but aren't shown.
    const applyRgb = (rgb) => {
        const target = { ...colorTarget, rgb, name: nameForRgb(rgb) }
        setColorTarget(target)
        setHexText(rgbToHex(rgb))
        persist({ colorTarget: target })
    }
    const onChannel = (idx) => (e) => {
        const rgb = (colorTarget?.rgb || [0, 0, 0]).slice()
        rgb[idx] = Number(e.target.value)
        applyRgb(rgb)
    }
    // Typed/pasted hex: always reflect what's typed, and apply once it's valid.
    const onHexInput = (e) => {
        const v = e.target.value
        setHexText(v)
        const rgb = hexToRgb(v)
        if (rgb) {
            const target = { ...colorTarget, rgb, name: nameForRgb(rgb) }
            setColorTarget(target)
            persist({ colorTarget: target })
        }
    }
    const onOperator = (key) => {
        setOperator(key)
        persist({ operator: key })
    }
    const onValue = (e) => {
        setValue(e.target.value)
        persist({ value: e.target.value })
    }
    const onCompareMode = (m) => {
        setCompareMode(m)
        const patch = { compareMode: m }
        // Default the comparison variable to the first "other" data var.
        if (m === 'variable' && !compareVar) {
            const first = otherDataVars[0]?.id || ''
            setCompareVar(first)
            patch.compareVar = first
        }
        persist(patch)
    }
    const onCompareVar = (e) => {
        setCompareVar(e.target.value)
        persist({ compareVar: e.target.value })
    }
    const onKeyName = (e) => {
        setKeyName(e.target.value)
        persist({ keyName: e.target.value })
    }
    const onMouseButton = (e) => {
        setMouseButton(e.target.value)
        persist({ mouseButton: e.target.value })
    }
    const onButtonId = (e) => {
        setButtonId(e.target.value)
        persist({ buttonId: e.target.value })
    }
    const onToggleId = (e) => {
        setToggleId(e.target.value)
        persist({ toggleId: e.target.value })
    }
    const onGestureName = (e) => {
        setGestureName(e.target.value)
        persist({ gestureName: e.target.value })
    }
    const onSpeechWord = (e) => {
        setSpeechWord(e.target.value)
        persist({ speechWord: e.target.value })
    }
    const onOcrText = (e) => {
        setOcrText(e.target.value)
        persist({ ocrText: e.target.value })
    }
    const onPathId = (e) => {
        setPathId(e.target.value)
        persist({ pathId: e.target.value })
    }
    const onPathDir = (e) => {
        setPathDir(e.target.value)
        persist({ pathDir: e.target.value })
    }
    const onPathTarget = (e) => {
        setPathTarget(e.target.value)
        persist({ pathTarget: e.target.value })
    }

    return (
        <div className="outertabDiv-modals">
            {showColorPicker && (
                <CameraColorPicker
                    onPick={(rgb) => {
                        applyRgb(rgb)
                        setShowColorPicker(false)
                    }}
                    onClose={() => setShowColorPicker(false)}
                />
            )}
            <img
                src={closeImg}
                alt="close"
                onClick={props.handleClose}
                style={{
                    position: 'absolute',
                    width: '7vh',
                    top: '-2%',
                    zIndex: '100',
                    right: '-2%',
                    // transform: 'translate(-100%,-50%)',
                    cursor: 'pointer',
                }}
            ></img>
            <div
                className="FlowchartDecisionPanel_Container"
                style={{ gridTemplateRows: '24% 40% 36%' }}
            >
                {/* Row 1: If the value of [source] is */}
                <div
                    style={{ position: 'relative' }}
                    className="Flowsub_Container1"
                >
                    <div id="Flowcontent1_text1" style={{ color: 'black' }}>
                        <p
                            style={{
                                position: 'absolute',
                                top: '50%',
                                right: '7%',
                                transform: 'translate(0%, -50%)',
                            }}
                        >
                            If the value of
                        </p>
                    </div>
                    <div id="Flowcontent1_text2" style={{ color: 'black' }}>
                        <div
                            style={{
                                border: '2px solid black',
                                position: 'absolute',
                                top: '50%',
                                left: '0%',
                                transform: 'translate(0%, -50%)',
                                width: '100%',
                                height: '40%',
                                borderRadius: '15px',
                                fontFamily: 'Halcyon_Medium',
                            }}
                        >
                            <select
                                className="AiCondPanel_boxSelect"
                                value={source}
                                onChange={onSource}
                            >
                                {sources.length === 0 && (
                                    <option value="">No source</option>
                                )}
                                {sources.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div id="Flowcontent1_text3" style={{ color: 'black' }}>
                        <p
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '7%',
                                transform: 'translate(0, -50%)',
                            }}
                        >
                            is
                        </p>
                    </div>
                </div>

                {/* Row 2: operator buttons + labels (one grid, 5 columns) */}
                <div
                    className="Flowsub_Container2"
                    style={{ gridTemplateColumns: 'repeat(5, 20%)' }}
                >
                    {OPERATORS.map((op, i) => {
                        // Recognition and colour are boolean matches, so only
                        // equals / not-equals apply.
                        const opDisabled =
                            isBooleanSource &&
                            !['equalTo', 'notEqualTo'].includes(op.key)
                        return (
                            <div key={op.key} id={`Flowcontent_div${i + 1}`}>
                                <img
                                    alt={op.label}
                                    src={renderPrgImage(
                                        operator === op.key
                                            ? `${op.img}Active`
                                            : `${op.img}InActive`
                                    )}
                                    onClick={() =>
                                        !opDisabled && onOperator(op.key)
                                    }
                                    style={{
                                        opacity: opDisabled ? 0.3 : 1,
                                        cursor: opDisabled
                                            ? 'not-allowed'
                                            : 'pointer',
                                    }}
                                />
                            </div>
                        )
                    })}
                    {OPERATORS.map((op, i) => (
                        <p
                            key={`t-${op.key}`}
                            id={`Flowsensors-txt${i + 1}`}
                            style={{ top: '47%' }}
                        >
                            {op.label}
                        </p>
                    ))}
                </div>

                {/* Row 3: for Face Recognition -> model/class/region;
                    otherwise the value slider (+ X/Y/XY axis for tracking). */}
                <div className="AiCondPanel_valueRow">
                    {isClassRecognition ? (
                        <>
                            <span className="AiCondPanel_label">with</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={selectedModelId}
                                    onChange={onModel}
                                >
                                    {camModels.length === 0 && (
                                        <option value="">No model</option>
                                    )}
                                    {camModels.map((m) => (
                                        <option
                                            key={m.modelId}
                                            value={m.modelId}
                                        >
                                            {m.modelName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">of</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={selectedClassId}
                                    onChange={onClass}
                                >
                                    <option value="none">Select Class</option>
                                    {classList.map((c) => (
                                        <option
                                            key={c.id}
                                            value={c.id}
                                            disabled={!c.trained}
                                        >
                                            {c.className}
                                            {c.trained ? '' : ' (not trained)'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {!isSpeech && (
                                <>
                                    <span className="AiCondPanel_label">
                                        in region
                                    </span>
                                    <div className="AiCondPanel_selectBox">
                                        <select
                                            className="AiCondPanel_boxSelect"
                                            value={selectedRegionId}
                                            onChange={onRegion}
                                        >
                                            <option value="none">
                                                No Region (Full Frame)
                                            </option>
                                            <option value="all">
                                                All Regions
                                            </option>
                                            {roiRegions.map((r) => (
                                                <option key={r.id} value={r.id}>
                                                    {r.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            <span className="AiCondPanel_label">is</span>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="1"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {value}
                            </span>
                        </>
                    ) : isPartCount ? (
                        <>
                            <span className="AiCondPanel_label">of</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={selectedModelId}
                                    onChange={onModel}
                                >
                                    {camModels.length === 0 && (
                                        <option value="">No model</option>
                                    )}
                                    {camModels.map((m) => (
                                        <option
                                            key={m.modelId}
                                            value={m.modelId}
                                        >
                                            {m.modelName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">class</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={selectedClassId}
                                    onChange={onClass}
                                >
                                    <option value="none">
                                        Any (all blobs)
                                    </option>
                                    {classList.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.className}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">is</span>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="50"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {value}
                            </span>
                        </>
                    ) : isColor ? (
                        <div className="AiCondPanel_colorBlock">
                            <div className="AiCondPanel_colorPickRow">
                                <span className="AiCondPanel_label">
                                    appears like
                                </span>
                                <span
                                    className="AiCondPanel_colorPreview"
                                    style={{
                                        background: `rgb(${(
                                            colorTarget?.rgb || [0, 0, 0]
                                        ).join(',')})`,
                                    }}
                                />
                                <input
                                    className="AiCondPanel_hexInput"
                                    type="text"
                                    spellCheck={false}
                                    placeholder="#RRGGBB"
                                    value={hexText}
                                    onChange={onHexInput}
                                />
                                {/* <span className="AiCondPanel_valueLabel">
                                    {colorTarget?.name}
                                </span> */}
                                <button
                                    type="button"
                                    className="AiCondPanel_pickBtn"
                                    onClick={() => setShowColorPicker(true)}
                                    title="Pick a colour from the camera"
                                >
                                    Pick from camera
                                </button>
                            </div>
                            <div className="AiCondPanel_colorParams">
                                {RGB_CHANNELS.map((ch, idx) => (
                                    <div
                                        className="AiCondPanel_paramCell"
                                        key={ch.key}
                                    >
                                        <span
                                            className="AiCondPanel_paramLabel"
                                            style={{ color: ch.color }}
                                        >
                                            {ch.key}
                                        </span>
                                        <input
                                            className="AiCondPanel_slider"
                                            style={{ background: ch.gradient }}
                                            type="range"
                                            min="0"
                                            max="255"
                                            value={colorTarget?.rgb?.[idx] ?? 0}
                                            onChange={onChannel(idx)}
                                        />
                                        <span className="AiCondPanel_paramValue">
                                            {colorTarget?.rgb?.[idx] ?? 0}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : isPathCross ? (
                        // "of [target] crosses [path] towards [direction]"
                        <>
                            <span className="AiCondPanel_label">of</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={pathTarget}
                                    onChange={onPathTarget}
                                >
                                    {pathTargetOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">crosses</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={pathId}
                                    onChange={onPathId}
                                >
                                    {drawnPaths.length === 0 && (
                                        <option value="">
                                            Draw a path in Configure › Lines
                                        </option>
                                    )}
                                    {drawnPaths.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">towards</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={pathDir}
                                    onChange={onPathDir}
                                >
                                    {PATH_DIR_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : isSpeechWord ? (
                        <>
                            <span className="AiCondPanel_label">the word</span>
                            <input
                                className="AiCondPanel_textInput"
                                type="text"
                                placeholder="e.g. Start"
                                value={speechWord}
                                onChange={onSpeechWord}
                            />
                            <span className="AiCondPanel_label">is said</span>
                        </>
                    ) : isOcr ? (
                        <>
                            <span className="AiCondPanel_label">the text</span>
                            <input
                                className="AiCondPanel_textInput"
                                type="text"
                                placeholder="e.g. STOP"
                                value={ocrText}
                                onChange={onOcrText}
                            />
                            <span className="AiCondPanel_label">is seen</span>
                        </>
                    ) : isMarker ? (
                        <>
                            <span className="AiCondPanel_label">id</span>
                            <input
                                className="AiCondPanel_textInput"
                                type="number"
                                min="0"
                                placeholder="e.g. 5"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_label">type</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={markerDict}
                                    onChange={(e) => {
                                        saveMarkerDict(e.target.value)
                                        setMarkerDict(e.target.value)
                                    }}
                                >
                                    {MARKER_DICTS.map((d) => (
                                        <option key={d.key} value={d.key}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : isMotion ? (
                        <>
                            <span className="AiCondPanel_label">
                                is detected
                            </span>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="1"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {Number(value) === 1 ? 'Yes' : 'No'}
                            </span>
                        </>
                    ) : isGesture ? (
                        <>
                            <span className="AiCondPanel_label">is</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={gestureName}
                                    onChange={onGestureName}
                                >
                                    {GESTURE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : isKey ? (
                        <>
                            <span className="AiCondPanel_label">key</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={keyName}
                                    onChange={onKeyName}
                                >
                                    {KEY_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">
                                is pressed
                            </span>
                        </>
                    ) : isMouse ? (
                        <>
                            <span className="AiCondPanel_label">mouse</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={mouseButton}
                                    onChange={onMouseButton}
                                >
                                    {MOUSE_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">
                                is clicked
                            </span>
                        </>
                    ) : isScreenButton ? (
                        <>
                            <span className="AiCondPanel_label">the</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={buttonId}
                                    onChange={onButtonId}
                                >
                                    {screenButtons.map((b) => (
                                        <option key={b.id} value={b.id}>
                                            {b.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">
                                is pressed
                            </span>
                        </>
                    ) : isScreenToggle ? (
                        <>
                            <span className="AiCondPanel_label">the</span>
                            <div className="AiCondPanel_selectBox">
                                <select
                                    className="AiCondPanel_boxSelect"
                                    value={toggleId}
                                    onChange={onToggleId}
                                >
                                    {screenToggles.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="AiCondPanel_label">is ON</span>
                        </>
                    ) : isVarFlag ? (
                        <>
                            <span className="AiCondPanel_label">is</span>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="1"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {Number(value) === 1 ? 'On' : 'Off'}
                            </span>
                        </>
                    ) : isVarCounter ? (
                        <>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="255"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {value}
                            </span>
                        </>
                    ) : isVarData ? (
                        <>
                            {/* With two data variables, offer comparing against
                                a typed value OR the other variable. */}
                            {showCompareToggle && (
                                <div className="AiCondPanel_axis">
                                    <button
                                        type="button"
                                        className={`AiCondPanel_axisBtn ${
                                            compareMode !== 'variable'
                                                ? 'AiCondPanel_axisActive'
                                                : ''
                                        }`}
                                        onClick={() => onCompareMode('value')}
                                    >
                                        Value
                                    </button>
                                    <button
                                        type="button"
                                        className={`AiCondPanel_axisBtn ${
                                            compareMode === 'variable'
                                                ? 'AiCondPanel_axisActive'
                                                : ''
                                        }`}
                                        onClick={() => onCompareMode('variable')}
                                    >
                                        Variable
                                    </button>
                                </div>
                            )}
                            {showCompareToggle && compareMode === 'variable' ? (
                                <div className="AiCondPanel_selectBox">
                                    <select
                                        className="AiCondPanel_boxSelect"
                                        value={
                                            compareVar ||
                                            otherDataVars[0]?.id ||
                                            ''
                                        }
                                        onChange={onCompareVar}
                                    >
                                        {otherDataVars.map((v) => (
                                            <option key={v.id} value={v.id}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <input
                                    className="AiCondPanel_textInput"
                                    type="number"
                                    value={value}
                                    onChange={onValue}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <input
                                className="AiCondPanel_slider"
                                type="range"
                                min="0"
                                max="255"
                                value={value}
                                onChange={onValue}
                            />
                            <span className="AiCondPanel_valueLabel">
                                {value}
                            </span>
                        </>
                    )}
                    {/* Trajectory overlay toggle for tracking rules — placed at
                        the end of the row in its own context. */}
                    {isPathCross && (
                        <div className="AiCondPanel_traceRow">
                            <span className="AiCondPanel_label">Trace</span>
                            <ToggleSwitch
                                size="md"
                                tone="orange"
                                checked={showTrace}
                                onChange={toggleTrace}
                                ariaLabel="Toggle trajectory overlay"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AiConditionPanel
